import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { ArrowLeft, FileDown, Sparkles, Plus, Trash2 } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import jsPDF from "jspdf";

const PCT_DOMAIN = [0, 100];
const LINE_DOT = { r: 3 };
const BAR_RADIUS = [6, 6, 0, 0];

export default function TherapistStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useSession();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState([]);
  const [badges, setBadges] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [assignTitle, setAssignTitle] = useState("");
  const [assignNote, setAssignNote] = useState("");

  const refresh = async () => {
    const [s, prog, b, a, ex] = await Promise.all([
      api.getStudent(id),
      api.listProgress(id),
      api.listBadges(id),
      api.listAssignments({ student_id: id }),
      api.listExercises(),
    ]);
    setData(s); setProgress(prog); setBadges(b); setAssignments(a); setExercises(ex);
  };

  useEffect(() => { refresh(); }, [id]);

  const trend = [...progress].reverse().map((p, i) => ({
    n: i + 1, accuracy: p.accuracy, stars: p.stars,
  }));

  const levelStats = ["shape", "letter", "word", "sentence"].map(l => ({
    level: l,
    count: progress.filter(p => p.level === l).length,
    avg: (() => {
      const xs = progress.filter(p => p.level === l);
      if (!xs.length) return 0;
      return Math.round(xs.reduce((a, b) => a + b.accuracy, 0) / xs.length);
    })(),
  }));

  const generateAi = async (audience) => {
    setAiLoading(true); setAiSummary("");
    try {
      const r = await api.aiSummary({ student_id: id, audience });
      setAiSummary(r.summary);
    } catch {
      toast.error("AI summary failed");
    } finally {
      setAiLoading(false);
    }
  };

  const downloadPdf = async () => {
    const rep = await api.report(id);
    const doc = new jsPDF();
    let y = 16;
    doc.setFontSize(20);
    doc.text(`Progress Report: ${rep.student.name}`, 14, y); y += 8;
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date(rep.generated_at).toLocaleString()}`, 14, y); y += 10;

    doc.setFontSize(14); doc.text("Summary", 14, y); y += 6;
    doc.setFontSize(11);
    doc.text(`Total sessions: ${rep.summary.total_sessions}`, 14, y); y += 6;
    doc.text(`Average accuracy: ${rep.summary.average_accuracy}%`, 14, y); y += 6;
    doc.text(`Total stars: ${rep.summary.total_stars}`, 14, y); y += 6;
    doc.text(`By level: shape=${rep.summary.by_level.shape}, letter=${rep.summary.by_level.letter}, word=${rep.summary.by_level.word}, sentence=${rep.summary.by_level.sentence}`, 14, y); y += 10;

    if (aiSummary) {
      doc.setFontSize(14); doc.text("AI Summary", 14, y); y += 6;
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(aiSummary, 180);
      doc.text(lines, 14, y); y += lines.length * 5 + 6;
    }

    doc.setFontSize(14); doc.text("Recent Sessions", 14, y); y += 6;
    doc.setFontSize(10);
    rep.progress.slice(0, 15).forEach(p => {
      const line = `${new Date(p.completed_at).toLocaleDateString()}  ${p.exercise_title}  ${p.accuracy}%  ${p.stars}★  (${p.duration_seconds}s)`;
      doc.text(line, 14, y); y += 5;
      if (y > 270) { doc.addPage(); y = 16; }
    });

    doc.setFontSize(14); y += 4; doc.text("Badges", 14, y); y += 6;
    doc.setFontSize(10);
    rep.badges.forEach(b => { doc.text(`• ${b.name} — ${b.description}`, 14, y); y += 5; });

    doc.save(`${rep.student.name}-report.pdf`);
    toast.success("Report downloaded");
  };

  const toggleEx = (id) => setSelectedExercises(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const createAssignment = async () => {
    if (!assignTitle || selectedExercises.length === 0) {
      toast.error("Add a title and pick exercises"); return;
    }
    await api.createAssignment({
      therapist_id: userId, student_id: id, exercise_ids: selectedExercises,
      title: assignTitle, note: assignNote,
    });
    setAssignOpen(false);
    setAssignTitle(""); setAssignNote(""); setSelectedExercises([]);
    refresh();
    toast.success("Assignment created");
  };

  const removeAssignment = async (aid) => {
    await api.deleteAssignment(aid); refresh();
  };

  if (!data) return <div className="p-10">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-border px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate(`/therapist`)} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-therapist">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          <img src={data.user.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
          <div>
            <div className="font-semibold">{data.user.name}</div>
            <div className="text-xs text-muted-foreground">Age {data.profile.age}</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Clinical overview</h1>
          <div className="flex gap-2">
            <Button onClick={downloadPdf} variant="outline" data-testid="download-pdf-btn">
              <FileDown className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
              <DialogTrigger asChild>
                <Button data-testid="open-assign-btn"><Plus className="h-4 w-4 mr-2" /> New assignment</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Create assignment</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="aTitle">Title</Label>
                    <Input id="aTitle" value={assignTitle} onChange={e => setAssignTitle(e.target.value)} data-testid="assign-title" />
                  </div>
                  <div>
                    <Label htmlFor="aNote">Note for student</Label>
                    <Textarea id="aNote" value={assignNote} onChange={e => setAssignNote(e.target.value)} data-testid="assign-note" />
                  </div>
                  <div>
                    <Label>Pick exercises ({selectedExercises.length})</Label>
                    <div className="grid md:grid-cols-2 gap-2 max-h-64 overflow-auto p-2 border rounded-lg" data-testid="exercise-picker">
                      {exercises.map(ex => (
                        <label key={ex.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={selectedExercises.includes(ex.id)}
                            onCheckedChange={() => toggleEx(ex.id)}
                            data-testid={`pick-${ex.id}`}
                          />
                          <span>{ex.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createAssignment} data-testid="save-assignment-btn">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 bg-white">
            <div className="font-semibold mb-2">Accuracy trend</div>
            <div className="h-56" data-testid="accuracy-chart">
              <ResponsiveContainer>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="n" />
                  <YAxis domain={PCT_DOMAIN} />
                  <Tooltip />
                  <Line type="monotone" dataKey="accuracy" stroke="hsl(var(--primary))" strokeWidth={2} dot={LINE_DOT} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="p-5 bg-white">
            <div className="font-semibold mb-2">Sessions by level</div>
            <div className="h-56" data-testid="levels-chart">
              <ResponsiveContainer>
                <BarChart data={levelStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="level" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563EB" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="p-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-orange-500" /> AI progress summary</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => generateAi("therapist")} disabled={aiLoading} data-testid="gen-ai-therapist">For therapist</Button>
              <Button size="sm" variant="outline" onClick={() => generateAi("parent")} disabled={aiLoading} data-testid="gen-ai-parent">For parent</Button>
            </div>
          </div>
          <div className="text-sm text-foreground whitespace-pre-wrap min-h-[60px]" data-testid="ai-summary-text">
            {aiLoading ? "Generating…" : (aiSummary || "Click a button above to generate an AI-written summary.")}
          </div>
        </Card>

        <Card className="p-5 bg-white">
          <div className="font-semibold mb-3">Assignments ({assignments.length})</div>
          {assignments.length === 0 ? (
            <div className="text-sm text-muted-foreground">No assignments yet.</div>
          ) : (
            <ul className="divide-y" data-testid="assignment-list">
              {assignments.map(a => (
                <li key={a.id} className="py-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground">{a.exercise_ids.length} exercises · {a.status}</div>
                    {a.note && <div className="text-sm mt-1">{a.note}</div>}
                  </div>
                  <button onClick={() => removeAssignment(a.id)} className="text-destructive hover:text-destructive/80" data-testid={`del-assign-${a.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5 bg-white">
          <div className="font-semibold mb-3">Recent sessions</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead className="text-muted-foreground">
                <tr><th className="text-left py-2">Date</th><th className="text-left">Exercise</th><th className="text-left">Level</th><th className="text-right">Accuracy</th><th className="text-right">Stars</th></tr>
              </thead>
              <tbody>
                {progress.slice(0, 15).map(p => (
                  <tr key={p.id} className="border-t">
                    <td className="py-2">{new Date(p.completed_at).toLocaleDateString()}</td>
                    <td>{p.exercise_title}</td>
                    <td className="capitalize text-muted-foreground">{p.level}</td>
                    <td className="text-right">{p.accuracy}%</td>
                    <td className="text-right">{p.stars}★</td>
                  </tr>
                ))}
                {progress.length === 0 && (
                  <tr><td colSpan="5" className="py-4 text-center text-muted-foreground">No sessions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
    </div>
  );
}
